/**
 * 브라우저 호환 암호화 유틸리티 클래스
 * Web Crypto API를 사용하여 안전한 암호화를 구현합니다.
 */
export class CryptoUtils {
  private static readonly ITERATIONS = 100000
  private static readonly KEY_LENGTH = 32

  /**
   * 문자열을 Uint8Array로 변환
   */
  private static stringToUint8Array(str: string): Uint8Array {
    return new TextEncoder().encode(str)
  }

  /**
   * Uint8Array를 문자열로 변환
   */
  private static uint8ArrayToString(array: Uint8Array): string {
    return new TextDecoder().decode(array)
  }

  /**
   * Uint8Array를 hex 문자열로 변환
   */
  private static uint8ArrayToHex(array: Uint8Array): string {
    return Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * hex 문자열을 Uint8Array로 변환
   */
  private static hexToUint8Array(hex: string): Uint8Array {
    const bytes = new Uint8Array(hex.length / 2)
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16)
    }
    return bytes
  }

  /**
   * 안전한 랜덤 salt 생성
   * @param length - salt 길이 (기본값: 16)
   * @returns 생성된 salt
   */
  static generateSalt(length: number = 16): string {
    const array = new Uint8Array(length)
    crypto.getRandomValues(array)
    return this.uint8ArrayToHex(array)
  }

  /**
   * AES-GCM을 사용한 대칭키 암호화
   * @param text - 암호화할 문자열
   * @param key - 암호화 키 (생략 시 자동 생성)
   * @returns 암호화된 결과와 키, IV를 포함한 객체
   */
  static async encryptAES(
    text: string,
    key?: string,
  ): Promise<{
    encrypted: string
    key: string
    iv: string
  }> {
    // 키 생성 또는 기존 키 사용
    const encryptionKey = key || this.generateSalt(32)
    const keyBuffer = this.hexToUint8Array(encryptionKey)

    // IV 생성
    const iv = crypto.getRandomValues(new Uint8Array(12))

    // 키 가져오기
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer as BufferSource,
      { name: 'AES-GCM' },
      false,
      ['encrypt'],
    )

    // 암호화
    const dataBuffer = this.stringToUint8Array(text)
    const encryptedBuffer = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
      },
      cryptoKey,
      dataBuffer as BufferSource,
    )

    return {
      encrypted: this.uint8ArrayToHex(new Uint8Array(encryptedBuffer)),
      key: encryptionKey,
      iv: this.uint8ArrayToHex(iv),
    }
  }

  /**
   * AES-GCM을 사용한 대칭키 복호화
   * @param encrypted - 암호화된 문자열
   * @param key - 암호화 키
   * @param iv - 초기화 벡터
   * @returns 복호화된 문자열
   */
  static async decryptAES(encrypted: string, key: string, iv: string): Promise<string> {
    try {
      const keyBuffer = this.hexToUint8Array(key)
      const ivBuffer = this.hexToUint8Array(iv)
      const encryptedBuffer = this.hexToUint8Array(encrypted)

      // 키 가져오기
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer as BufferSource,
        { name: 'AES-GCM' },
        false,
        ['decrypt'],
      )

      // 복호화
      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: ivBuffer as BufferSource,
        },
        cryptoKey,
        encryptedBuffer as BufferSource,
      )

      return this.uint8ArrayToString(new Uint8Array(decryptedBuffer))
    } catch (error) {
      console.error('복호화 중 오류 발생:', error)
      throw new Error('복호화에 실패했습니다.')
    }
  }
}

/**
 * 사용 예시:
 * // 1. AES 암호화/복호화 (비동기)
 * const aesResult = await CryptoUtils.encryptAES('sensitive data');
 * const decrypted = await CryptoUtils.decryptAES(aesResult.encrypted, aesResult.key, aesResult.iv);
 * console.log(decrypted); // 'sensitive data'
 */
